/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

const ScheduleFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [recurrence, setRecurrence] = useState('');
  const [courseId, setCourseId] = useState('');
  const [courses, setCourses] = useState([]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If this is a new event and we have initial date from calendar
    if (!isEdit && state?.initialDate) {
      const { start, end } = state.initialDate;
      setStart(start.toISOString().slice(0, 16));
      setEnd(end.toISOString().slice(0, 16));
    }
  }, [isEdit, state]);

  useEffect(() => {
    // fetch courses for dropdown
    setLoading(true);
    setError(null);
    api.get('/courses')
      .then(res => {
        setCourses(res.data.data || []);
        setError(null);
      })
      .catch(err => {
        console.error(err);
        setError(err.response?.data?.error || 'Failed to load courses');
      })
      .finally(() => setLoading(false));

    if (isEdit) {
      setLoading(true);
      api.get(`/schedules/${id}`)
        .then(res => {
          const e = res.data;
          setTitle(e.title);
          setDescription(e.description || '');
          setStart(e.start.slice(0,16));
          setEnd(e.end.slice(0,16));
          setRecurrence(e.recurrence || '');
          setCourseId(e.courseId || '');
          setError(null);
        })
        .catch(err => {
          console.error(err);
          setError(err.response?.data?.error || 'Failed to load schedule');
          navigate('/teacher/dashboard/schedule'); // <-- update here
        })
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, navigate]);

  const handleParse = async () => {
    if (!file) return alert('Select a file to parse');
    setLoading(true);
    setError(null);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await api.post('/schedules/parse', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      const e = res.data;
      // Redirect to preview page for parsed data
      navigate('/admin/dashboard/schedule/parse', { state: { parsed: e } });
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to parse schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    // Prepare form data (including optional file)
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('start', start);
    formData.append('end', end);
    formData.append('recurrence', recurrence);
    if (courseId) formData.append('courseId', courseId);
    if (file) formData.append('file', file);
    if (isEdit) await api.put(`/schedules/${id}`, formData);
    else await api.post('/schedules', formData);
    // Redirect to teacher dashboard after scheduling
    navigate('/teacher/dashboard/schedule');
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-md">
      <h1 className="text-2xl mb-4">{isEdit ? 'Edit' : 'New'} Event</h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block mb-1">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} required className="w-full border p-2" />
        </div>
        <div>
          <label className="block mb-1">Description</label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} className="w-full border p-2" rows={3} />
        </div>
        <div>
          <label className="block mb-1">Start</label>
          <input type="datetime-local" value={start} onChange={e => setStart(e.target.value)} required className="w-full border p-2" />
        </div>
        <div>
          <label className="block mb-1">End</label>
          <input type="datetime-local" value={end} onChange={e => setEnd(e.target.value)} required className="w-full border p-2" />
        </div>
        <div>
          <label className="block mb-1">Recurrence (RRULE)</label>
          <input value={recurrence} onChange={e => setRecurrence(e.target.value)} className="w-full border p-2" placeholder="e.g. FREQ=WEEKLY;BYDAY=MO" />
        </div>
        <div>
          <label className="block mb-1">Course</label>
          <select value={courseId} onChange={e => setCourseId(e.target.value)} className="w-full border p-2">
            <option value="">School-wide</option>
            {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <div>
            <label className="block mb-1">Upload File</label>
            <input type="file" accept=".pdf,.docx" onChange={e => setFile(e.target.files[0])} className="border p-2" />
          </div>
          <button type="button" onClick={handleParse} className="mt-6 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded">
            Parse File
          </button>
        </div>
        <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded">
          {isEdit ? 'Update' : 'Create'}
        </button>
      </form>
    </div>
  );
};

export default ScheduleFormPage;
